import { WarningRounded } from '@mui/icons-material';
import { Paper, Stack, Typography, useTheme } from '@mui/material';
import { memo, type JSX } from 'react';
import QuantityPicker from 'src/components/common/quantity-picker/QuantityPicker';
import { VisibilityControllable } from 'src/hooks/common/ui/visibility-control';
import { useLocale } from '@repo/i18n';
import { bookingWidgetInputStyle } from 'src/components/utils/jss/TextFieldStyleUtils';
import { useCustomizations } from 'src/components/utils/theme/customizations';
import { QuantityInputItem } from '@repo/widget-utils/booking/domains/shared/types';

type MultipleNumberInputProps = VisibilityControllable & {
    /** Pre-formatted input items with all calculations done */
    items: readonly QuantityInputItem[];

    /** Callback when quantity changes - receives string id (not number) */
    onChange: (id: string, value: number) => void;

    /** Optional pre-translated global error message (e.g., collection capacity exceeded) */
    globalError?: string;

    /** Optional message to display when inputs are disabled */
    disabledMessage?: string;

    /** Optional label text (defaults to "How many are going") */
    labelText?: string;

    // Styling props (keep these from old component)
    labelColor?: string;
    backgroundColor?: string;
    color?: string;
    buttonColor?: string;
    variant?: 'outlined' | 'filled';
    padding?: number;
    border?: string;
};

const MultipleNumberInput = memo(function MultipleNumberInput(
    props: MultipleNumberInputProps,
): JSX.Element {
    const {
        items,
        onChange,
        globalError,
        disabledMessage,
        labelText,
        backgroundColor,
        color,
        padding = 0,
        border,
        labelColor,
        buttonColor,
    } = props;

    const theme = useTheme();
    const { t } = useLocale();
    const customizations = useCustomizations();

    const colors = bookingWidgetInputStyle(customizations, backgroundColor, color);

    return (
        <Stack gap={0.5}>
            <Stack>
                {(labelText === undefined || labelText.length > 0) && (
                    <Typography
                        component="div"
                        sx={{
                            color: labelColor ?? customizations.bookingWidgetColorContrast,
                            fontSize: '0.85rem',
                            marginBottom: '4px',
                        }}
                    >
                        {labelText ?? t.how_many_are_going}
                    </Typography>
                )}

                <Paper
                    elevation={0}
                    sx={{
                        py: theme.spacing(padding),
                        px: 0,
                        transition: 'all 0.5s ease-in-out',
                        background: colors.backgroundColor,
                        color: colors.color,
                        border: border,
                        borderRadius: customizations.borderRadius + 'px',
                        overflow: 'hidden',
                        width: '100%',
                    }}
                >
                    <Stack justifyContent="center" alignItems="center">
                        {/* Global error (collection-level) */}
                        {globalError && (
                            <Stack
                                role="alert"
                                aria-live="assertive"
                                direction="row"
                                px={1.5}
                                pt={1.5}
                                pb={1}
                                gap={1}
                                justifyContent="center"
                                alignItems="center"
                            >
                                <WarningRounded
                                    aria-hidden="true"
                                    sx={{ fill: colors.color, width: 18, height: 18 }}
                                />
                                <Typography
                                    lineHeight={1}
                                    pb="2px"
                                    color={colors.color}
                                    fontSize={14}
                                >
                                    {globalError}
                                </Typography>
                            </Stack>
                        )}

                        {/* Render items - simple mapping, no logic */}
                        {items.map((item) => (
                            <QuantityPicker
                                key={item.id}
                                value={item.value}
                                name={item.label}
                                subText={item.subText}
                                onChange={(value) => onChange(item.id, value)}
                                minValue={item.min}
                                maxValue={item.max}
                                disabled={item.disabled}
                                error={item.error}
                                ignoreMaxValue={item.ignoreMaxValue}
                                color={color}
                                backgroundColor={backgroundColor}
                                buttonColor={buttonColor}
                            />
                        ))}
                    </Stack>
                </Paper>
            </Stack>

            {/* Disabled message (pre-computed) */}
            {disabledMessage && (
                <Typography
                    role="status"
                    color={customizations.bookingWidgetColorContrast}
                    fontSize={14}
                    fontStyle="italic"
                >
                    {disabledMessage}
                </Typography>
            )}
        </Stack>
    );
});

MultipleNumberInput.displayName = 'MultipleNumberInput';

export default MultipleNumberInput;
